import React from 'react'
import { AccountOption, List, l, PrimaryNavigation } from '@manychat/manyui'

import { useAppDispatch, useAppSelector } from 'reduxTyped'
import { makeAccountFavorite } from 'apps/profile/actions/profileActions'
import { Account } from 'apps/profile/models/Page/helpers'
import { RegistrationSection } from 'apps/registration/interfaces/registration'
import { ConnectChannelModalSource, DefaultChannelsList } from 'common/core/constants/ChannelType'
import { useExploreModeConnectChannel } from 'common/core/hooks/useExploreModeConnectChannel'
import { getCurrentAccount } from 'common/core/selectors/appSelectors'
import { getCurrentAccountIsDummyMode } from 'common/core/selectors/currentAccountSelectors'
import { useMediaQuery } from 'utils/commonHooks'
import { typedRouting } from 'utils/router/typedRouting'
import { analyticsService } from 'utils/services/analytics'

import { BottomContent } from './BottomContent'
import { useAvailableRoutes } from './hooks/useAvailableRoutes'
import { NavItem } from './NavItem'
import { DUMMY_MODE_EXCLUDED_IDS, NavigationRoute } from './navigationRoutes'
import { getAccountStatusLozenge } from './utils'

interface SidebarProps {
  isSidebarHidden: boolean
  isLoading?: boolean
  accounts: Account[]
  onToggleSidebar: () => void
  onSelectAccountClick: () => void
}

export const Sidebar = ({
  isSidebarHidden,
  isLoading,
  accounts,
  onToggleSidebar,
  onSelectAccountClick,
}: SidebarProps) => {
  const dispatch = useAppDispatch()
  const account = useAppSelector(getCurrentAccount)
  const isDummyMode = useAppSelector(getCurrentAccountIsDummyMode)
  const connectChannelModal = useExploreModeConnectChannel()

  const isMobile = useMediaQuery('(max-width: 900px)')
  const availableRoutes = useAvailableRoutes()

  const handleItemClick = (item: NavigationRoute) => {
    if (item.url === '/dashboard') {
      analyticsService.sendEvent(`SIDEBAR.HOME_TAB.CLICKED`)
    }

    if (isMobile) {
      onToggleSidebar()
    }

    if (isDummyMode && !DUMMY_MODE_EXCLUDED_IDS.has(item.id)) {
      connectChannelModal.open(DefaultChannelsList, ConnectChannelModalSource.SIDEBAR_DUMMY_MODE)
    }
  }

  const handlePinToggle = (account: AccountOption, isFavorite: boolean) => {
    dispatch(makeAccountFavorite({ accountId: String(account.value), isFavorite }))
  }

  const handleAccountAddClick = () => {
    const connectionLink = typedRouting.linkBuilder.registration({
      section: RegistrationSection.CHANNEL_CONNECTION,
    })
    window.location.href = connectionLink
  }

  const handleAccountChange = (account: AccountOption) => {
    const id = account.value
    location.assign(`/fb${id}/dashboard`)
  }

  const selectedAccount: AccountOption = {
    label: account?.title || '',
    value: account?.page_id || '',
    lozenge: getAccountStatusLozenge(account?.pro_status),
    avatarUrl: account?.ava_big || account?.ava_small || undefined,
  }

  const accountOptions = accounts.map<AccountOption>((account) => ({
    label: account.name,
    value: account.id,
    lozenge: getAccountStatusLozenge(account.accountStatus),
    avatarUrl: account.avatar || undefined,
    isPinned: account.isFavorite,
  }))

  return (
    <PrimaryNavigation
      ariaLabel={l.translate('Main navigation')}
      skipToContentTarget="#appContent"
      skipToContentLabel={l.translate('Skip to main content')}
      data-test-id="sidebar"
      expanded={!isSidebarHidden}
      accounts={accountOptions}
      selectedAccount={selectedAccount}
      onAccountChange={handleAccountChange}
      onAccountPinToggle={handlePinToggle}
      onAccountAddClick={handleAccountAddClick}
      onSelectAccountClick={onSelectAccountClick}
      isLoading={isLoading}
      menuContent={
        <List className="p-a-0 d-flex flex-col">
          {availableRoutes.map((item) => (
            <NavItem
              key={item.id}
              item={item}
              className="m-l-xxs"
              expanded={!isSidebarHidden}
              onClick={handleItemClick}
              mode={isDummyMode && !DUMMY_MODE_EXCLUDED_IDS.has(item.id) ? 'button' : 'link'}
            />
          ))}
        </List>
      }
      bottomContent={<BottomContent />}
      onClick={onToggleSidebar}
    />
  )
}
